import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Activity } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { markFeedEvent } from '../../db/calendar.js';
import type { AgendaItem } from '../../db/calendar.js';
import type { NoteEventPreset } from '../journal/DayNotes.js';
import { formatDay } from '../../format.js';
import { ActivityFields } from './EventEditor.js';
import { useSheetDismiss } from '../../gestures/SwipeProvider.js';

/**
 * Ein eingelesener Termin: ändern lässt er sich nur in Apple/Google. Hier kann
 * man ihn als Aktivität markieren (zählt dann in der Bilanz) oder als
 * ausgefallen.
 */
export function FeedEventSheet({
  item,
  source,
  onWriteNote,
  onClose,
}: {
  item: AgendaItem;
  source: string;
  onWriteNote: (preset: NoteEventPreset) => void;
  onClose: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ids = { activity: useId(), activityMin: useId() };
  const [activity, setActivity] = useState<Activity | ''>(item.activity ?? '');
  const [minutes, setMinutes] = useState(String(item.activityMin ?? item.durationMin ?? 60));
  const [cancelled, setCancelled] = useState(item.cancelled);
  const dirty =
    activity !== (item.activity ?? '') ||
    minutes !== String(item.activityMin ?? item.durationMin ?? 60) ||
    cancelled !== item.cancelled;
  const sheet = useSheetDismiss<HTMLFormElement>(onClose, dirty);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="feed-event-title"
      className="fixed inset-0 z-20 flex items-end justify-center bg-black/30 p-3"
    >
      <form
        ref={sheet}
        className="kw-glass flex w-full max-w-lg flex-col gap-3 rounded-[1.5rem] p-4"
        onSubmit={(event) => {
          event.preventDefault();
          const parsed = Number(minutes);
          void markFeedEvent(
            item.id,
            {
              activity: activity || null,
              activityMin: activity && Number.isInteger(parsed) && parsed > 0 ? parsed : null,
              cancelled,
            },
            db,
          ).then(onClose);
        }}
      >
        <h2 id="feed-event-title" className="font-display text-xl font-bold break-words">
          {item.title}
        </h2>
        <p className="text-sm text-ink-muted">
          {formatDay(item.date, 'long', i18n.language)} · {item.time ?? t('calendar.allDay')}
          {item.location ? ` · ${item.location}` : ''}
          <br />
          {t('calendar.fromSource', { source })}
        </p>
        <ActivityFields
          ids={ids}
          activity={activity}
          minutes={minutes}
          onActivity={setActivity}
          onMinutes={setMinutes}
        />
        <button
          type="button"
          aria-pressed={cancelled}
          onClick={() => setCancelled(!cancelled)}
          className="kw-toggle self-start"
        >
          {t('calendar.cancelled')}
        </button>
        <button
          type="button"
          onClick={() =>
            onWriteNote({ kind: 'feed', id: item.id, title: item.title, date: item.date })
          }
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('notes.writeAbout')}
        </button>
        <div className="flex gap-2">
          <button type="submit" className="kw-btn flex-1">
            {t('calendar.editor.save')}
          </button>
          <button type="button" onClick={onClose} className="kw-btn kw-btn-quiet flex-1">
            {t('nutrition.library.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
